import React, { useState } from "react";
import logo from "../../assets/logo.png";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import { useAuth } from "../../context/AuthProvider";
import { Menu, X } from "lucide-react";
import { FaDiscord } from "react-icons/fa";
import useSubscription from "../../hooks/useSubscription";
import dynamic from "next/dynamic";
import MobileMenu from "./MobileMenu";
import MessageIcon from "./MessageIcon.js";
import { useModal } from "../../context/ModalContext.js";
import { useT, useLocale } from "@jammable/ui-core/src/i18n";

// Header renders on every page. Statically importing UpgradeModal pulled its
// whole tree into every page's first load; it only opens on click.
const UpgradeModal = dynamic(() => import("../modals/UpgradeModal"), {
  ssr: false,
});
// Signed-in only (the user is known only in the browser, so these never
// render on the server): logged-out visitors, most of the traffic, don't
// download the notification list (date-fns) or the Radix dropdown menu.
// The placeholders keep the header from shifting while they load.
const NotificationIcon = dynamic(() => import("./NotificationIcon.js"), {
  ssr: false,
  loading: () => (
    <div className="tw-h-11 tw-w-11 tw-rounded-full tw-bg-jam-lav" />
  ),
});
const ProfileMenu = dynamic(() => import("./ProfileMenu"), {
  ssr: false,
  loading: () => (
    <div className="tw-h-11 tw-w-11 tw-shrink-0 tw-rounded-full tw-border-2 tw-border-solid tw-border-jam-line" />
  ),
});

// Design language F: the nav is one lavender pill, the current page a white
// chip inside it. "Create" points at the cover generator until the studio
// (/create) is the studio. Labels are message keys; hrefs stay English
// routes (Home becomes /es or /pt on those pages, see homeHref).
const NAV = [
  { href: "/", label: "nav.home", match: (p) => p === "/" },
  { href: "/create", label: "nav.create", match: (p) => p === "/create" },
  {
    href: "/models",
    label: "nav.voices",
    match: (p) => p === "/models" || p.startsWith("/ai-voices"),
  },
  { href: "/train", label: "nav.train", match: (p) => p === "/train" },
  {
    href: "/library",
    label: "nav.library",
    match: (p) => p === "/library" || p === "/history",
    authOnly: true,
  },
  { href: "/pricing", label: "nav.pricing", match: (p) => p === "/pricing" },
];

const Header = () => {
  const router = useRouter();
  const pathname = router.pathname;
  const { user } = useAuth();
  const t = useT();
  const { locale, fromUrl } = useLocale();
  // On /es and /pt pages "Home" is that language's home.
  const homeHref = fromUrl ? `/${locale}` : "/";

  const isAuthenticated = !!user;
  const { hasBasicPlan, hasCreatorPlan, hasPowerPlan } = useSubscription();
  const planName = hasPowerPlan()
    ? "Power"
    : hasCreatorPlan()
      ? "Creator"
      : hasBasicPlan()
        ? "Basic"
        : null;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { openModal } = useModal();

  const openUpgrade = () =>
    openModal("upgrade-modal", <UpgradeModal from="header-upgrade" />, {
      className: "tw-p-0",
    });

  return (
    <div className="tw-sticky tw-top-[var(--card-wall-h,0px)] tw-z-[100] tw-w-full tw-bg-white/95 tw-backdrop-blur">
      <div className="tw-mx-auto tw-flex tw-h-16 tw-max-w-[1280px] tw-items-center tw-gap-3 tw-px-4 md:tw-h-20 md:tw-px-8">
        <Link
          href="/"
          className="tw-flex tw-items-center tw-gap-2.5 tw-text-jam-ink tw-no-underline"
        >
          <Image
            src={logo}
            width={36}
            height={36}
            priority
            alt="Jammable"
            quality={100}
          />
          <span className="jam-display tw-text-[22px] tw-font-extrabold md:tw-text-2xl">
            Jammable
          </span>
        </Link>

        <nav className="tw-ml-auto tw-hidden tw-items-center tw-gap-1 tw-rounded-full tw-bg-jam-lav tw-p-[5px] lg:tw-flex">
          {NAV.filter((item) => !item.authOnly || isAuthenticated).map(
            (item) => {
              const isHome = item.href === "/";
              const active = isHome
                ? pathname === homeHref
                : item.match(pathname);
              return (
                <Link
                  key={item.href}
                  href={isHome ? homeHref : item.href}
                  className={`tw-rounded-full tw-px-[18px] tw-py-[9px] tw-text-sm tw-font-extrabold tw-no-underline tw-transition-colors ${
                    active
                      ? "tw-bg-white tw-text-jam-purple"
                      : "tw-text-jam-ink hover:tw-text-jam-purple"
                  }`}
                >
                  {t(item.label)}
                </Link>
              );
            },
          )}
        </nav>

        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
          {/* Deliberately outside the isAuthenticated gate - logged-out
              visitors are the ones most likely to want the community. */}
          <a
            href="https://discord.gg/jammable"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("nav.discordAria")}
            title={t("nav.discordJoin")}
            className="tw-hidden tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-[#5865F2] sm:tw-inline-flex"
          >
            <FaDiscord className="tw-size-5" />
          </a>

          {isAuthenticated && (
            <div className="tw-flex tw-items-center tw-gap-1">
              <NotificationIcon />
              <MessageIcon />
            </div>
          )}

          {isAuthenticated &&
            (planName ? (
              <span className="tw-hidden tw-rounded-full tw-bg-jam-lime tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink sm:tw-inline">
                {planName}
              </span>
            ) : (
              <button
                type="button"
                onClick={openUpgrade}
                className="tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-4 tw-py-2.5 tw-text-[13px] tw-font-extrabold tw-text-jam-ink"
              >
                {t("nav.getPlan")}
              </button>
            ))}

          {isAuthenticated && user?.avatar ? (
            <ProfileMenu user={user} />
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <Link
                href="/login"
                className="tw-rounded-full tw-bg-jam-lav tw-px-4 tw-py-3 tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline md:tw-px-5"
              >
                {t("nav.login")}
              </Link>
              <Link
                href="/signup"
                className="tw-hidden tw-rounded-full tw-bg-jam-ink tw-px-5 tw-py-3 tw-text-sm tw-font-extrabold tw-text-white tw-no-underline sm:tw-inline"
              >
                {t("nav.signupFree")}
              </Link>
            </div>
          )}

          <button
            type="button"
            aria-label={mobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lav tw-text-jam-ink lg:tw-hidden"
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <MobileMenu
          mobileMenuOpen={mobileMenuOpen}
          toggleMobileMenu={() => setMobileMenuOpen(false)}
          isAuthenticated={isAuthenticated}
          isCinematic={false}
        />
      )}
    </div>
  );
};

export default Header;
